/**
 * @fileoverview Main layout component with header and footer.
 */

import { Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPhone, FiMapPin, FiMail, FiExternalLink, FiFileText, FiShield } from 'react-icons/fi';
import { FaFacebookF, FaInstagram, FaTwitter, FaTiktok, FaYoutube, FaLinkedinIn } from 'react-icons/fa';
import { customerConfig } from '@/lib/config/customer.config';
import { BookingsBadge } from '@/components/layout/BookingsBadge';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';

/**
 * Get social media icon component by platform name
 */
function getSocialIcon(url: string) {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('facebook')) return FaFacebookF;
  if (lowerUrl.includes('instagram')) return FaInstagram;
  if (lowerUrl.includes('twitter') || lowerUrl.includes('x.com')) return FaTwitter;
  if (lowerUrl.includes('tiktok')) return FaTiktok;
  if (lowerUrl.includes('youtube')) return FaYoutube;
  if (lowerUrl.includes('linkedin')) return FaLinkedinIn;
  return FiExternalLink;
}

/**
 * Get platform name from URL
 */
function getPlatformName(url: string) {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('facebook')) return 'Facebook';
  if (lowerUrl.includes('instagram')) return 'Instagram';
  if (lowerUrl.includes('twitter') || lowerUrl.includes('x.com')) return 'Twitter';
  if (lowerUrl.includes('tiktok')) return 'TikTok';
  if (lowerUrl.includes('youtube')) return 'YouTube';
  if (lowerUrl.includes('linkedin')) return 'LinkedIn';
  return 'Social';
}

/**
 * Layout component wrapping all pages.
 */
export function Layout() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        {t('nav.skipToContent')}
      </a>

      {/* Header */}
      <header
        className="sticky top-0 z-50 bg-card/70 backdrop-blur-xl border-b border-border/50"
        role="banner"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-3"
              aria-label={`${customerConfig.businessName} - ${t('nav.home')}`}
            >
              {customerConfig.theme?.logoUrl ? (
                <img
                  src={customerConfig.theme.logoUrl}
                  alt=""
                  className="h-10 w-auto"
                  aria-hidden="true"
                />
              ) : null}
              <span className={customerConfig.theme?.logoUrl ? 'sr-only' : 'text-xl font-bold text-primary'}>
                {customerConfig.businessName}
              </span>
            </Link>

            <nav className="flex items-center gap-1 sm:gap-2" aria-label="Main navigation">
              {/* Phone - hidden on mobile */}
              <a
                href={`tel:${customerConfig.phone.replace(/\s/g, '')}`}
                className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                aria-label={`${t('nav.callUs')} ${customerConfig.phone}`}
              >
                <FiPhone className="h-4 w-4" aria-hidden="true" />
                <span>{customerConfig.phone}</span>
              </a>

              {/* Bookings Badge */}
              <BookingsBadge />

              {/* Theme & Language - far right */}
              <div className="flex items-center gap-1 border-l border-border/50 ml-2 pl-2 sm:ml-3 sm:pl-3">
                <ThemeToggle />
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-1 py-8 md:py-12" role="main" tabIndex={-1}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-card/50 backdrop-blur-sm border-t border-border/50" role="contentinfo">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {customerConfig.businessName}
              </h3>
              {customerConfig.tagline && (
                <p className="text-sm text-muted-foreground mb-4">
                  {customerConfig.tagline}
                </p>
              )}
              {/* Social Links */}
              {customerConfig.socialLinks && (
                <div className="flex items-center gap-2">
                  {Object.entries(customerConfig.socialLinks).map(([key, url]) => {
                    if (!url || typeof url !== 'string') return null;
                    const Icon = getSocialIcon(url as string);
                    const platform = getPlatformName(url as string);
                    return (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted/50 text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                        aria-label={platform}
                        title={platform}
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-medium mb-3">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a
                    href={`tel:${customerConfig.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    <FiPhone className="h-4 w-4" />
                    {customerConfig.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${customerConfig.email}`}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    <FiMail className="h-4 w-4" />
                    {customerConfig.email}
                  </a>
                </li>
              </ul>
            </div>

            {/* Location */}
            <div>
              <h4 className="font-medium mb-3">{t('footer.location')}</h4>
              <address className="not-italic text-sm text-muted-foreground">
                <a
                  href={customerConfig.address.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 hover:text-foreground transition-colors"
                >
                  <FiMapPin className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {customerConfig.address.street}
                    <br />
                    {customerConfig.address.suburb}, {customerConfig.address.state}{' '}
                    {customerConfig.address.postalCode}
                  </span>
                </a>
              </address>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="mt-8 pt-6 border-t border-border/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <p>
                  &copy; {new Date().getFullYear()} {customerConfig.businessName}. {t('footer.allRightsReserved')}
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to="/terms"
                    className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                  >
                    <FiFileText className="h-3.5 w-3.5" />
                    {t('legal.termsOfService')}
                  </Link>
                  <span className="text-border">•</span>
                  <Link
                    to="/privacy"
                    className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                  >
                    <FiShield className="h-3.5 w-3.5" />
                    {t('legal.privacyPolicy')}
                  </Link>
                </div>
              </div>
              <a
                href="https://buungroup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <span>{t('footer.builtBy')}</span>
                <img
                  src="https://buungroup.com/logo/logo.svg"
                  alt="Buun Group"
                  className="h-5 w-auto transition-transform duration-500 group-hover:rotate-[360deg]"
                />
                <span className="font-medium">Buun Group</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
